"use client";

import { useTranslations } from "next-intl";
import { DebugSection, DebugTableRows } from "../components/debug-primitives";
import type {
  SelectedOrganization,
  VisualContextTabProps,
} from "./visual-context-tab.types";

export type { VisualContextTabProps, SelectedOrganization };

export function VisualContextTab({
  user,
  organizations,
  selectedOrganization,
  sessionContext,
  authIsLoading,
  authError,
}: VisualContextTabProps) {
  const t = useTranslations("debug.visual");
  const activeOrg: SelectedOrganization | null = selectedOrganization ?? null;
  const identityFallback = authIsLoading ? t("loading") : t("unavailable");

  return (
    <>
      <DebugSection title={t("user")} accent="border-blue-500">
        <DebugTableRows
          rows={[
            {
              label: t("name"),
              value: user ? t("available") : identityFallback,
            },
            {
              label: t("role"),
              node: (
                <span className="px-2.5 py-0.5 rounded-md bg-blue-500/10 text-blue-600 dark:text-blue-400 text-sm font-medium">
                  {user?.contextKind || identityFallback}
                </span>
              ),
            },
            {
              label: t("identityPayload"),
              value: t("notCollected"),
            },
          ]}
        />
      </DebugSection>

      <DebugSection title={t("tenantOrganization")} accent="border-emerald-500">
        <DebugTableRows
          rows={[
            {
              label: t("type"),
              node: (
                <div className="flex items-center gap-2">
                  <span>{activeOrg?.type || t("unavailable")}</span>
                  {activeOrg?.type && (
                    <span className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground text-sm font-mono">
                      {activeOrg.type}
                    </span>
                  )}
                </div>
              ),
            },
            {
              label: t("plan"),
              value: t("planUnavailable"),
            },
          ]}
        />
      </DebugSection>

      <DebugSection title={t("opaqueSession")} accent="border-amber-500">
        <DebugTableRows
          rows={[
            {
              label: t("sessionScope"),
              value: sessionContext.label,
            },
            {
              label: t("transportContract"),
              value: t("opaqueServerSession"),
            },
          ]}
        />
      </DebugSection>

      {organizations && organizations.length > 0 && (
        <DebugSection
          title={t("organizationAssignments", { count: organizations.length })}
          accent="border-blue-500"
          defaultOpen={false}
        >
          <DebugTableRows
            rows={organizations.map((org) => ({
              label: org.type || t("unavailable"),
              node: (
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-mono text-muted-foreground">
                    {t("identifierNotCollected")}
                  </span>
                  {org.type && (
                    <span className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground text-sm">
                      {org.type}
                    </span>
                  )}
                </div>
              ),
            }))}
          />
        </DebugSection>
      )}

      <DebugSection
        title={t("authState")}
        accent="border-slate-500"
        defaultOpen={false}
      >
        <DebugTableRows
          rows={[
            {
              label: t("isLoading"),
              node: (
                <span
                  className={
                    authIsLoading
                      ? "text-amber-500 text-sm"
                      : "text-muted-foreground/40 text-sm"
                  }
                >
                  {authIsLoading ? t("true") : t("false")}
                </span>
              ),
            },
            {
              label: t("error"),
              node: authError ? (
                <span className="text-red-400 text-sm break-all">
                  {authError}
                </span>
              ) : (
                <span className="text-muted-foreground/40 text-sm">{"—"}</span>
              ),
            },
          ]}
        />
      </DebugSection>
    </>
  );
}
